import * as $ from 'jquery';
import * as Backbone from 'backbone';
import * as Marionette from 'backbone.marionette';
import * as _ from 'underscore';
import moment from '../utils/moment';
import { on, Options, debounce } from '../utils/decorators';
import Detector from "../utils/detector";
import axios from 'axios';
import 'url-polyfill';
import 'slick-carousel';
import 'jquery-match-height';

const swiftype_engine_id = (<any>window).DJANGO.swiftypeEngineID;
const swiftype_site = (<any>window).DJANGO.swiftypeSite;

const per_page = 25;
const piece_json_base = '/musicdb/json/pieces/';
const artist_json_base = '/musicdb/json/artists/';

@Options({
    template: false,
    ui : {
        searchResults:      '[data-js-ui=search-results]',
        searchInput:        '.search-bar',
        searchClearBtn:     '[data-js-ui=search-clear-btn]',
    },
})

export default class SearchResultsView extends Marionette.View<Backbone.Model>{
    config?: object;
    detector?: Detector;

    constructor(options?:object, config?:object) {
        super(options);
        this.config = config || {};
        this.detector = new Detector();
    }

    buildURL(q, page?, tab?) {
        if (!q) {
            return '';
        }

        q = q.replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");


        let url = `?q=${q}`;

        if (page && page > 1) {
            url += `&page=${page}`;
        }

        if (tab && tab.length) {
            url += `&show=${tab}`;
        }

        return url;
    }

    makePaginationList(currentPage, totalPages, resultsPerPage, totalResults, query) {
        let pages = [];

        // pagination logic
        for(let i = 1; i<=totalPages; i++) {
            // skip showing links to pages that aren't either:
            //      * the first few pages
            //      * the last page
            //      * within a range of current page
            if(i > 3 && i < totalPages - 1 && Math.abs(i - currentPage) > 3) {
                continue;
            }

            // the index of the first result on the given page
            let offset = i * resultsPerPage;

            pages.push({
                number: i,
                min: offset,
                max: Math.min(totalResults, offset + resultsPerPage),
                isCurrent: i == currentPage,
                link: this.buildURL(query, i, this.queryParams().get('show'))
            });
        }

        return pages;
    }

    async showResults(result, suggestedQuery?, originalQuery?) {
        let totalResults = result.info.page.total_result_count;
        let resultsPerPage = result.info.page.per_page;
        let currentPage = result.info.page.current_page;
        let totalPages = result.info.page.num_pages;
        let start = (currentPage - 1) * resultsPerPage; // index of first item on this page
        let end = Math.min((currentPage * resultsPerPage) - 1, totalResults); // index of last item on this page
        let prev = currentPage > 1 ? this.buildURL(result.info.page.query, currentPage - 1, this.queryParams().get('show')) : null; // link to previous page, if exists
        let next = currentPage < totalPages ? this.buildURL(result.info.page.query,  currentPage + 1, this.queryParams().get('show')) : null; // link to next page, if exists
        let pages = this.makePaginationList(currentPage, totalPages, resultsPerPage, totalResults, result.info.page.query); // list of all pages to show in pagination
        let allTab = this.buildURL(result.info.page.query, null);
        let artistsTab = this.buildURL(result.info.page.query, null, 'artist');
        let eventsTab = this.buildURL(result.info.page.query, null, 'performance');
        let pressTab = this.buildURL(result.info.page.query, null, 'press-release');

        // sanitised list of results
        let resultsList = result.records.page.map(item => {
            // remove host & domain from url
            item.nice_url = new URL(item.url).pathname;

            // trim whitespace
            if (_.has(item.highlight, 'body')) {
                item.body = item.highlight.body;
            } else if (_.has(item.highlight, 'sections')) {
                item.body = item.highlight.sections;
            } else {
                item.body = null;
            }

            if (item.body) {
                item.body = item.body.replace(/^(?:(?![\w])[^\w<]*)/, '').trim();

                if(item.body.length > 275) {
                    let regex = /(.{0,275}\S*)/m;
                    item.body = item.body.match(regex)[0] + '&hellip;';
                }
            }

            // return item
            return item;
        });

        let [ rankedResults, unrankedResults ] = _.partition(resultsList, (item:any) => {
            return item._score === 1;
        });

        // Sort unranked results with special ranking
        unrankedResults.sort((a, b) => {
            const isSeries = a.type === 'series' && b.type === 'series';
            const isFestival = a.type === 'festival' && b.type === 'festival';

            if (isSeries || isFestival) {
                return b.internal_weight - a.internal_weight || b._score - a._score;
            }

            return b._score - a._score;
        });

        resultsList = [].concat(rankedResults, unrankedResults);

        // list of blocks which are rendered on list. different blockTypes are rendered differently
        let blocks = [];

        // for each result...
        for(let index = 0; index < resultsList.length; index++) {
            let item = resultsList[index];

            if (item.hidden) {
                continue;
            }

            switch(item.type) {
                case 'performance':
                    blocks.push(this.createPerformancesScrollerBlock(item, resultsList, blocks, currentPage))
                    break;
                case 'faq':
                    blocks.push(this.createFAQsBlock(item, resultsList, blocks, currentPage));
                    break;
                case 'press-release':
                    blocks.push(this.createPressBlock(item, resultsList, blocks, currentPage));
                    break;
                case 'artist':
                    blocks.push(this.createArtistBlock(item, resultsList, blocks, currentPage));
                    break;
                case 'piece':
                    blocks.push(this.createPieceBlock(item, resultsList, blocks, currentPage));
                    break;
                case 'series':
                    blocks.push(this.createSeriesBlock(item, resultsList, blocks, currentPage));
                    break;
                case 'festival':
                    blocks.push(this.createFestivalBlock(item, resultsList, blocks, currentPage));
                    break;
                default:
                    blocks.push({
                        blockType: 'default',
                        ...item
                    })
                    break;
            }
        }

        const results = new SearchResultsTemplateView({
            totalResults: totalResults,
            start: start,
            end: end,
            resultsPerPage: resultsPerPage,
            currentPage: currentPage,
            pages: pages,
            totalPages: totalPages,
            prev: prev,
            next: next,
            queryString: result.info.page.query,
            blocks: blocks,
            suggestedQuery: suggestedQuery,
            suggestedQueryUrl: this.buildURL(suggestedQuery),
            originalQuery: originalQuery,
        });

        const tabs = new SearchTabsTemplateView({
            allTab: allTab,
            artistsTab: artistsTab,
            eventsTab: eventsTab,
            pressTab: pressTab,
        });

        this.ui.searchResults.removeClass('search-results--loading');

        setTimeout(() => {
            results.render();
            tabs.render();
            this.setActiveTab();
            // this.

            // then slickify event sliders
            $('.search-result--events-scroller .search-result__list').slick(this.config);

            // fix heights?
            this.detector.initMatchHeight();

            // stop modal links
            this.detector.initDisablePopupLinks();
        }, 1000);
    }

    createPerformancesScrollerBlock(item, list, blocks, currentPage) {
        /*
            * do an api search for query, but only for performances
            * make a block which is a list of those performances
            * set all other performances to hidden
        */
        // we only do the group if we don't already have group
        // and if we're on the first page
        if (blocks.some(x => x.blockType === 'performances') === false && currentPage === 1) {
            let allPerformanceItems = list
                .filter(x => x.type === 'performance')
                .filter(x => new Date(x.start_date).getTime() > new Date().getTime())
                .sort((a,b) => {
                    let aStart = new Date(a.start_date).getTime();
                    let bStart = new Date(b.start_date).getTime();

                    if (aStart == bStart) {
                        return b._score - a._score;
                    }

                    return aStart - bStart;
                });

            // grab the ids so we can hide these and not the rest
            const allPerformanceItemsIds = allPerformanceItems.map(x => x.id);

            // mark all in group as hidden
            list.forEach(x => {
                if (allPerformanceItemsIds.indexOf(x.id) !== -1) {
                    x.hidden = true;
                    x.booking_url = this.determineBookingUrl(x);
                    x.nice_date = this.nicePerformanceDate(x);
                }
            });

            if (allPerformanceItems.length) {
                return {
                    blockType: 'performances',
                    performances: allPerformanceItems,
                };
            }
        }

        let isInPast = new Date(item.start_date).getTime() < new Date().getTime();

        return {
            ...item,
            blockType: 'performance',
            booking_url: this.determineBookingUrl(item),
            nice_date: this.nicePerformanceDate(item),
            isInPast: isInPast,
        };
    }

    createFAQsBlock(item, list, blocks, currentPage) {
        /*
            * grab all the faqs in the current page of search results
            * make a block which is a list of those faqs
            * set all other performances to hidden
        */

        const allFAQsOnPage = list.filter(x => x.type === 'faq');

        let block = {
            blockType: 'faqs',
            title: 'Frequently Asked Questions',
            faqs: allFAQsOnPage,
        };

        list.forEach(x => {
            if(x.type === 'faq' && x.id !== item.id) {
                x.hidden = true;
            }
        });

        return block;
    }

    createPressBlock(item, list, blocks, currentPage) {
        return {
            ...item,
            blockType: 'press-release',
            nice_date: moment(item.date).format('MMM. D, YYYY'),
        }
    }

    createSeriesBlock(item, list, blocks, currentPage) {
        return {
            ...item,
            blockType: 'series',
        }
    }

    createFestivalBlock(item, list, blocks, currentPage) {
        return {
            ...item,
            blockType: 'festival',
        }
    }

    createArtistBlock(item, list, blocks, currentPage) {
        return {
            ...item,
            blockType: 'artist',
        }
    }

    createPieceBlock(item, list, blocks, currentPage) {
        return {
            ...item,
            blockType: 'piece',
        }
    }

    nicePerformanceDate(performance) {
        let date = moment(performance.start_date);

        return {
            month: date.format('MMM'),
            date: date.format('DD'),
            day: date.format('ddd'),
            time: date.format('h:mmA'),
            year: date.format('YYYY'),
        }
    }

    async loadMusicDBData(type, id) {
        let url = type == 'piece' ? piece_json_base : artist_json_base;
        return axios.get(url + id).then(response => {
            return _.mapObject(response.data.data, value => {
                return _.isString(value) ? _.escape(value) : value;
            });
        }).catch(error => {
            return {}
        })
    }

    determineBookingUrl(performance) {
        if (performance.ticketing_system_type === 'TESSITURA') {
            return performance.tessitura_url;
        }

        return performance.ticketmaster_url;
    }

    queryParams() {
        return new URLSearchParams(window.location.search);
    }

    makeSearchURL(searchTerm?) {
        let paramsObj = {
            q: searchTerm || this.queryParams().get('q'),
            per_page: per_page,
            spelling: 'always',
            engine_key: swiftype_engine_id
        }

        if (this.queryParams().has('page')) {
            paramsObj['page'] = this.queryParams().get('page');
        }

        // @ts-ignore
        const params = new URLSearchParams(paramsObj);

        params.append('highlight_fields[page][body][size]', 275 + 25); // we'll trim to 275, but give it a buffer
        params.append('highlight_fields[page][body][fallback]', true);
        params.append('filters[page][site]', swiftype_site);
        params.append('filters[page][type][]', 'festival');
        params.append('filters[page][type][]', 'performance');
        params.append('filters[page][type][]', 'series');
        params.append('filters[page][type][]', 'faq');
        params.append('filters[page][type][]', 'artist');
        params.append('filters[page][type][]', 'piece');
        params.append('filters[page][type][]', 'page');
        params.append('filters[page][hide_from_calendar]', '0');

        if (this.queryParams().has('show')) {
            params.append('filters[page][type]', this.queryParams().get('show'));
        }

        return `https://api.swiftype.com/api/v1/public/engines/search.json?` + params.toString();
    }

    setActiveTab() {
        let show = this.queryParams().get('show');

        $('.search-tabs__tab').removeClass('search-tabs__tab--active')

        let activeTab = $('[data-search-tab="' + show + '"]')

        if (activeTab.length > 0) {
            activeTab.addClass('search-tabs__tab--active')
        } else {
            $('[data-search-tab="all"]').addClass('search-tabs__tab--active')
        }
    }

    onRender() {
        let q = this.queryParams().get('q');

        this.setActiveTab();

        this.ui.searchInput.val(q);

        axios.get(this.makeSearchURL()).then(originalResponse => {
            const data = originalResponse.data;

            let suggestion = null;

            if (_.has(data.info.page, 'spelling_suggestion')) {
                suggestion = data.info.page.spelling_suggestion.text;
            }

            if (data.record_count) {
                return this.showResults(data, suggestion, null);
            }

            if (suggestion && !data.record_count) {
                return axios.get(this.makeSearchURL(suggestion)).then(suggestionResponse => {
                    return this.showResults(suggestionResponse.data, null, q);
                });
            }

            return this.showResults(data, null, null);
        });
    }
}

const templateSettings: object = {
    evaluate: /<@([\s\S]+?)@>/g,
    interpolate: /<@=([\s\S]+?)@>/g,
    escape: /<@-([\s\S]+?)@>/g,
};

const SearchResultsTemplateView = Marionette.View.extend({
    el: "[data-js-ui=search-results-template]",

    render: function() {
        const template = _.template(
            $('#search-results-template').html(), templateSettings
        );

        this.$el.html( template(this.options) );
    }
});

const SearchTabsTemplateView = Marionette.View.extend({
    el: "[data-js-ui=search-tabs-template]",

    render: function() {
        const template = _.template(
            $('#search-tabs-template').html(), templateSettings
        );

        this.$el.html( template(this.options) );
    }
});



// WEBPACK FOOTER //
// ./@modules/views/search-results-view.ts